import React from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import { SemanticICONS } from 'semantic-ui-react';
import IntlCrawlableLink from '../../IntlCrawlableLink';
import { useIntl, IntlShape } from 'react-intl';
import LanguageDropdown from '../../LanguageDropdown';
import AccountDropdown from '../../AccountDropdown';
import NetworkStatusIndicator from 'app/components/NetworkStatusIndicator';
import UpdateButton from 'app/components/UpdateButton';
import FormContact from 'app/components/Form/Contact';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';

import './styles.scss';

type NavSubMenuItem = {
  messageId: string;
  titleMessageId?: string;
  routes: string[];
};

type NavMenuItem = {
  menuName: string;
  subMenu: NavSubMenuItem[];
};

type NavIconItem = {
  icon: SemanticICONS;
  routes: string[];
  titleMessageId?: string;
  isLoggedInNeeded?: boolean;
  hasNotSubscribedNeeded?: boolean;
};

type NavTextItem = {
  messageId: string;
  routes: string[];
  titleMessageId?: string;
  isLoggedInNeeded?: boolean;
  hasNotSubscribedNeeded?: boolean;
};

type NavItem = NavMenuItem | NavIconItem | NavTextItem;

type NavbarFullSizeProps = {
  leftItems: NavItem[];
  middleItems: NavItem[];
  isLoggedIn: boolean;
  hasSubscribed: boolean;
  style?: React.CSSProperties;
  renderNavigationLink: (
    routes: string[],
    content: React.ReactNode,
    titleMessageId?: string,
    className?: string
  ) => React.ReactNode;
  getUserNameOnLinkClick?: () => void;
  switchFullScreenMode?: () => void;
};

const NavbarFullSize: React.FC<NavbarFullSizeProps> = ({
  leftItems,
  middleItems,
  isLoggedIn,
  hasSubscribed,
  style,
  renderNavigationLink,
  getUserNameOnLinkClick,
  switchFullScreenMode,
}) => {
  const intl = useIntl();
  const { electron } = (window as any).nodeModules ?? {};

  const isFullScreen = (): boolean | undefined => {
    if (!electron) return undefined;
    return electron.remote.getCurrentWindow().isFullScreen();
  };

  const renderNavigationSubmenu = (item: NavMenuItem) => {
    const menuName = intl.formatMessage({ id: item.menuName });
    const pathname = window.location.pathname.replace('C:/', '');
    const isActiveMenu = item.subMenu.some((subItem) =>
      subItem.routes.some((route) => `/${intl.locale}${route}` === pathname)
    );

    return (
      <Dropdown
        as="li"
        text={menuName}
        className={`link item menu-dropdown ${isActiveMenu ? 'active' : ''}`}
        key={item.menuName}
      >
        <Dropdown.Menu as="ul" className="menu-dropdown__content">
          {item.subMenu.map((subItem: NavSubMenuItem) => {
            const name = intl.formatMessage({ id: subItem.messageId });
            const title = intl.formatMessage({ id: subItem.titleMessageId });
            const isActiveSubMenu = subItem.routes.some(
              (route) => `/${intl.locale}${route}` === pathname
            );
            return (
              <li
                key={subItem.messageId}
                className={`menu-dropdown__item ${isActiveSubMenu ? 'active' : ''}`}
              >
                <IntlCrawlableLink
                  url={subItem.routes[0]}
                  className="item menu-dropdown__link"
                  onClick={getUserNameOnLinkClick}
                  title={title}
                >
                  {name}
                </IntlCrawlableLink>
              </li>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderNavItems = (items: NavItem[]) => {
    return items.map((item) => {
      if ('isLoggedInNeeded' in item && item.isLoggedInNeeded === true && !isLoggedIn) {
        return '';
      }
      if (
        'hasNotSubscribedNeeded' in item &&
        item.hasNotSubscribedNeeded === true &&
        hasSubscribed
      ) {
        return '';
      }
      if ('icon' in item) {
        return renderNavigationLink(
          item.routes,
          <Icon name={item.icon} className="headerNav" />,
          item.titleMessageId,
          'item'
        );
      }
      if ('menuName' in item) {
        return renderNavigationSubmenu(item as NavMenuItem);
      }
      return renderNavigationLink(
        (item as NavTextItem).routes,
        intl.formatMessage({ id: (item as NavTextItem).messageId }),
        (item as NavTextItem).titleMessageId,
        'item'
      );
    });
  };

  const renderRightItems = () => {
    const { formatMessage } = intl;
    return (
      <li className="right">
        {process.env.PLATFORM === 'website' && <LanguageDropdown />}
        <div className="flex center">
          <div className="flex center">
            <UpdateButton />
          </div>
        </div>
        <FormContact />
        <AccountDropdown />
        {process.env.PLATFORM === 'electron' && isAcquisitionSoftware() && (
          <NetworkStatusIndicator />
        )}
        {electron && (
          <>
            <button
              type="button"
              className="flex center right-button"
              onClick={() => electron.remote.BrowserWindow.getFocusedWindow().minimize()}
              title={formatMessage({ id: 'header.close.tooltip' })}
            >
              <Icon name="minus" />
            </button>
            <button
              type="button"
              className="flex center right-button"
              onClick={switchFullScreenMode}
              title={
                isFullScreen()
                  ? formatMessage({ id: 'header.minimize.tooltip' })
                  : formatMessage({ id: 'header.maximize.tooltip' })
              }
            >
              {isFullScreen() ? (
                <Icon name="window restore outline" />
              ) : (
                <Icon name="square outline" />
              )}
            </button>
            <button
              type="button"
              className="flex center right-button right-button--close"
              onClick={() => electron.remote.app.quit()}
              title={formatMessage({ id: 'header.close.tooltip' })}
            >
              <Icon name="close" />
            </button>
          </>
        )}
      </li>
    );
  };

  const thereAreMiddleItems = middleItems.length !== 0;

  return (
    <nav className="ui container nav-header nav" style={style}>
      <ul className="ui large secondary inverted pointing menu navbarFullSize">
        {renderNavItems(leftItems)}
        {thereAreMiddleItems && <span className="nav__separator" />}
        {renderNavItems(middleItems)}
        {renderRightItems()}
      </ul>
    </nav>
  );
};

export default NavbarFullSize;
